import React, { useRef, useState } from "react";
import { api, formatApiError, fmtINR } from "../lib/apiClient";
import { X, UploadSimple, FileXls, FileCode, CheckCircle, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

// Ledger master import: Tally XML export or Excel/CSV → preview (new / exists / new groups) → apply.
export function LedgerImportModal({ onClose, onDone }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [result, setResult] = useState(null);

  const preview = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/accounting/ledgers/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      setPlan(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      const rows = plan.rows.filter(r => r.status === "new" || updateExisting).map(({ name, group, opening_balance, opening_type, gstin }) => ({ name, group, opening_balance, opening_type, gstin }));
      const { data } = await api.post("/accounting/ledgers/import", { rows, new_groups: plan.new_groups, update_existing: updateExisting }, { timeout: 120000 });
      setResult(data);
      toast.success(`Imported ${data.created} ledger${data.created !== 1 ? "s" : ""}${data.groups_created ? `, ${data.groups_created} new group${data.groups_created !== 1 ? "s" : ""}` : ""}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={busy ? undefined : onClose} data-testid="ledger-import-modal">
      <div className="bg-white rounded-lg w-full max-w-4xl shadow-2xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="font-display font-semibold inline-flex items-center gap-2"><UploadSimple size={18} className="text-brand" /> Import Ledger Master</div>
          <button onClick={onClose} disabled={busy} className="btn-ghost" data-testid="ledger-import-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          {!plan && !result && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div className="border border-border rounded-md p-3 space-y-1"><div className="font-semibold inline-flex items-center gap-1"><FileCode size={14} /> Tally XML</div>
                  <div className="text-ink-muted">In Tally: Gateway → Export → Masters → Ledgers/Groups → XML. Groups, parents and opening balances are read automatically.</div></div>
                <div className="border border-border rounded-md p-3 space-y-1"><div className="font-semibold inline-flex items-center gap-1"><FileXls size={14} /> Excel / CSV</div>
                  <div className="text-ink-muted">Columns: <b>Name</b>, <b>Under</b> (group), <b>Opening Balance</b>, <b>Dr/Cr</b>, optional GSTIN. Header names are matched loosely.</div></div>
              </div>
              <button onClick={() => fileRef.current?.click()} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="ledger-import-pick">
                {busy ? <CircleNotch size={16} className="animate-spin" /> : <UploadSimple size={16} />} {busy ? "Reading file…" : "Choose file (XML / XLSX / CSV)"}
              </button>
              <input ref={fileRef} type="file" accept=".xml,.xlsx,.xls,.csv" className="hidden" data-testid="ledger-import-file"
                onChange={e => { const f = e.target.files?.[0]; if (f) preview(f); e.target.value = ""; }} />
            </div>
          )}
          {plan && !result && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="ledger-import-summary">
                <span className="status-pill status-success">{plan.summary.new} new</span>
                <span className="status-pill status-neutral">{plan.summary.exists} already exist</span>
                {plan.summary.new_groups > 0 && <span className="status-pill status-info">{plan.summary.new_groups} new group{plan.summary.new_groups !== 1 ? "s" : ""}: {plan.new_groups.map(g => g.name).join(", ")}</span>}
                <label className="ml-auto inline-flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={updateExisting} onChange={e => setUpdateExisting(e.target.checked)} data-testid="ledger-import-update-existing" /> Update group / opening of existing ledgers</label>
              </div>
              <div className="table-wrapper max-h-[50vh]">
                <table className="table-base text-xs">
                  <thead><tr><th>Ledger</th><th>Under</th><th className="text-right">Opening</th><th>GSTIN</th><th>Status</th></tr></thead>
                  <tbody>
                    {plan.rows.map((r, i) => (
                      <tr key={`${r.name}-${i}`} data-testid={`ledger-import-row-${r.status}`}>
                        <td className="font-semibold">{r.name}</td>
                        <td>{r.group}{r.group_status === "new" && <span className="ml-1 status-pill status-info">new group</span>}</td>
                        <td className="text-right tabular">{fmtINR(r.opening_balance)} {r.opening_type.toUpperCase()}</td>
                        <td className="font-mono">{r.gstin || "—"}</td>
                        <td>{r.status === "new" ? <span className="status-pill status-success">will create</span>
                          : <span className="status-pill status-neutral">exists{r.existing_group && r.existing_group !== r.group ? ` (under ${r.existing_group})` : ""}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setPlan(null)} disabled={busy} className="btn-secondary" data-testid="ledger-import-back">Choose another file</button>
                <button onClick={apply} disabled={busy || (plan.summary.new === 0 && !updateExisting)} className="btn-primary inline-flex items-center gap-1.5" data-testid="ledger-import-apply">
                  {busy ? <CircleNotch size={14} className="animate-spin" /> : <CheckCircle size={14} weight="bold" />} Import {updateExisting ? plan.summary.total : plan.summary.new} ledger{(updateExisting ? plan.summary.total : plan.summary.new) !== 1 ? "s" : ""}
                </button>
              </div>
            </div>
          )}
          {result && (
            <div className="text-center py-6 space-y-3" data-testid="ledger-import-result">
              <CheckCircle size={40} weight="fill" className="text-brand mx-auto" />
              <div className="font-display text-lg font-semibold">Import complete</div>
              <div className="text-sm text-ink-muted">{result.created} created · {result.updated} updated · {result.skipped} skipped (already existed) · {result.groups_created} group{result.groups_created !== 1 ? "s" : ""} created</div>
              <button onClick={onClose} className="btn-primary" data-testid="ledger-import-done">Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
